import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';

import {
  FinancialPeriod,
  FinancialPeriodResponse,
} from '../interfaces/financialPeriods.interfaces';
import { FinancialPeriodsPayload } from '../interfaces/payload.interface';

export const financialPeriodsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['FinancialPeriods'],
});

export const financialPeriodsApi = financialPeriodsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET Financial Periods
     */
    getFinancialPeriods: builder.query<
      FinancialPeriodResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async ({ profileId, page, search, perPage, sort, type_sort }) => {
        try {
          const { data } = await mainApi.get<FinancialPeriodResponse>('/financial-periods', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              ...(sort && { sort }),
              type_sort: type_sort || 'desc',
              search,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'FinancialPeriods', id } as const)),
              { type: 'FinancialPeriods', id: 'LIST' },
            ]
          : [{ type: 'FinancialPeriods', id: 'LIST' }],
    }),

    /**
     * @GET Financial periods by id
     */
    getFinancialPeriod: builder.query<FinancialPeriod, { profileId: number; fperiodId: number }>({
      queryFn: async ({ profileId, fperiodId }) => {
        try {
          const { data } = await mainApi.get<FinancialPeriod>(`/financial-periods/${fperiodId}`, {
            params: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, errro, { fperiodId }) => [
        // { type: 'FinancialPeriods', id: 'LIST' }, // optional, invalidate on all process
        { type: 'FinancialPeriods', fperiodId }, // invalidate only with period Id
      ],
    }),

    /**
     * @GET all Financial periods Availables
     */
    getAvailablesFinancialPeriods: builder.query<FinancialPeriod[], { profileId: Number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<FinancialPeriod[]>('/financial-periods', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'FinancialPeriods', id: 'LIST' }],
    }),

    /**
     * @POST create FinancialPeriod
     */
    addFinancialPeriodLevel: builder.mutation<
      any,
      { profileId: number; financialPeriodPayload: Partial<FinancialPeriodsPayload> }
    >({
      queryFn: async ({ profileId, financialPeriodPayload }) => {
        try {
          const { data } = await mainApi.post<any>('/financial-periods', {
            user_profile_id: profileId,
            ...financialPeriodPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'FinancialPeriods', id: 'LIST' }],
    }),

    /**
     * @PUT update financial period
     */
    updateFinancialPeriod: builder.mutation<
      any,
      {
        profileId: number;
        fperiodId: number;
        financialPeriodPayload: Partial<FinancialPeriodsPayload>;
      }
    >({
      queryFn: async ({ profileId, fperiodId, financialPeriodPayload }) => {
        try {
          const { data } = await mainApi.put<any>(`/financial-periods/${fperiodId}`, {
            user_profile_id: profileId,
            ...financialPeriodPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { fperiodId }) => [
        { type: 'FinancialPeriods', id: 'LIST' }, //invalidate all
        { type: 'FinancialPeriods', fperiodId }, // invalidate only with period Id
      ],
    }),

    /**
     * @DELETE remove financial period
     */
    deleteFinancialPeriod: builder.mutation<any, { profileId: number; fperiodId: number }>({
      queryFn: async ({ profileId, fperiodId }) => {
        try {
          const { data } = await mainApi.delete(`/financial-periods/${fperiodId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { fperiodId }) => [
        { type: 'FinancialPeriods', id: 'LIST' },
        { type: 'FinancialPeriods', fperiodId },
      ],
    }),

    //
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetFinancialPeriodsQuery,
  useGetFinancialPeriodQuery,
  useLazyGetFinancialPeriodQuery,
  useGetAvailablesFinancialPeriodsQuery,
  useAddFinancialPeriodLevelMutation,
  useUpdateFinancialPeriodMutation,
  useDeleteFinancialPeriodMutation,
} = financialPeriodsApi;
